/**
 * ULTIMATE — SKILL THỨ 8, do NGƯỜI CHƠI BẤM TAY (không auto, không tốn mana).
 *
 * Người dùng chốt: *"Nút bấm xuất hiện khi tôi click vào nhân vật, trên màn hình trò chơi hiện ra
 * skill của nhân vật đó"* ⇒ mỗi class có ĐÚNG 1 ultimate riêng, hiện thành 1 nút dưới dãy 7 ô chiêu
 * trong thẻ tướng (xem `src/ui/hud.ts`).
 *
 * VÌ SAO LÀ FILE DỮ LIỆU RIÊNG (không nhét vào `data/classes.ts`):
 *   · Ultimate KHÁC hẳn 7 chiêu thường: không `manaCost`, không `unlockWave`, hồi 3 PHÚT, có thời
 *     lượng hiệu lực riêng, và phần LỚN luật của nó nằm ở `sim/world.ts` (nuốt quái, bản sao, máy
 *     bay, thu phục…). Để chung sẽ làm `SkillDef` phình ra với nửa tá trường chỉ 1 chiêu dùng.
 *   · `ULT_DURATION` bên `sim/` phải khớp `duration` ở đây — xem chú thích `duration` bên dưới.
 *
 * ⚠️ SỐ LIỆU Ở ĐÂY LÀ HẰNG SỐ CÂN BẰNG, KHÔNG phải chỗ mô tả hiệu ứng: hiệu ứng thật do
 * `World.castUltimate()` trong `sim/world.ts` thi. Sửa `desc` mà không sửa `sim/` là mô tả sai game.
 */

import type { ClassId } from '../sim/types';

/**
 * HỒI 3 PHÚT cho MỌI ultimate (người dùng chốt con số này, không phân biệt class).
 *
 * ⚠️ Đây là NGUỒN SỰ THẬT của con số 180; `sim/world.ts` khai `ULT_COOLDOWN` cùng giá trị. Hai bên
 * cố ý tách vì `sim/` không được import `data/` theo chiều ngược (xem quy ước đầu `sim/types.ts`),
 * và vì ultimate dùng chung 1 nhịp hồi nên không cần đổi theo màn/ class.
 */
export const ULT_COOLDOWN = 180;

export interface UltimateDef {
  id: string;
  /** tên tiếng Việt (dữ liệu gốc — bản EN tra theo `id` ở `i18n/content-en.ts`) */
  name: string;
  /** mô tả tiếng Việt — PHẢI khớp luật thật trong `sim/world.ts` */
  desc: string;
  /**
   * THỜI LƯỢNG HIỆU LỰC (giây).
   *
   * `0` = KHÔNG có đồng hồ đếm ngược: hiệu lực kéo dài tới khi điều kiện riêng kết thúc (chỉ class
   * Đẻ mướn dùng — quân thu phục sống tới khi bị đánh chết, hoặc hết màn).
   */
  duration: number;
  /** giây hồi giữa 2 lần dùng — mọi class đều `ULT_COOLDOWN` */
  cooldown: number;
  /** màu nhận diện của ultimate (vẽ hiệu ứng + viền nút) */
  color: number;
}

/**
 * BIẾN HÌNH CỦA ULTIMATE — art nào thay cho hình class, và phóng to bao nhiêu.
 *
 * Người dùng: *"Sao ví dụ như Strength dùng ult biến hình mà không đổi hình ảnh nhân vật thành Dã
 * trư khổng lồ?"* ⇒ ultimate biến hình phải đổi cả HÌNH. Bảng này là NGUỒN SỰ THẬT duy nhất cho
 * phần hình; `render/unitview.ts` đọc nó khi dựng rig.
 *
 * `art` = tên file `art/ai/<art>-parts.png` (lưới 3×3 bộ phận, nền magenta — cùng chuẩn với class).
 * `scale` = hệ số phóng to so với `TARGET_CHAR_HEIGHT` (104 đơn vị).
 * KHÔNG có khoá ⇒ ultimate đó không biến hình (chỉ đổi chỉ số/ hiệu ứng).
 */
export const ULT_FORMS: Partial<Record<ClassId, { art: string; scale: number }>> = {
  /*
   * Dã trư khổng lồ — người dùng chốt phóng to **1.8×** (~187 đơn vị so với 104), đủ "khổng lồ"
   * mà không che hết màn.
   */
  strength: { art: 'boar', scale: 1.8 },
  /*
   * ⚠️ AGILITY KHÔNG khai ở đây — người dùng chốt *"Phân thân 3 nhân vật y như agility"*:
   * bản sao dùng CHÍNH art của Gái ngành, chỉ khác độ trong suốt + tím nhạt để biết con nào là
   * bản sao (xem `render/unitview.ts`, nhánh `u.isClone`). Vẽ art mới cho bản sao là SAI yêu cầu.
   *
   * 3 ultimate còn lại KHÔNG biến hình NHÂN VẬT ⇒ không đổi `scale`/ art thân. Art riêng của chúng
   * là hiệu ứng (bom xăng · máy bay · dấu thu phục), dùng ở `render/fx.ts` — xem `ULT_ART` dưới.
   */
};

/**
 * ART HIỆU ỨNG CỦA ULTIMATE (không phải art thân nhân vật).
 *
 * Người dùng: *"2: Cả 4 nhưng theo logic"* ⇒ chỉ Strength BIẾN HÌNH thân; 3 ultimate kia cần art
 * cho HIỆU ỨNG của chúng, không phải hình nhân vật:
 *   · street — quả bom xăng (chai + giẻ cháy) bay theo đường ném + vệt lửa;
 *   · army   — máy bay bay ngang + quả bom rơi;
 *   · mother — dấu hào quang trên quái ĐÃ THU PHỤC (không che hình quái vì nó sẽ trở lại làm quái).
 * `scale` không dùng ở đây; cỡ do lớp hiệu ứng quyết định.
 */
export const ULT_ART: Partial<Record<ClassId, { fx: string }>> = {
  street: { fx: 'xebom' },
  army: { fx: 'maybay' },
  mother: { fx: 'thuphuc-mark' },
};

export const ULTIMATES: Record<ClassId, UltimateDef> = {
  // ------------------------------------------------------------------ Dân tập gym
  strength: {
    id: 'ultGiantBoar',
    name: 'Dã Trư Khổng Lồ',
    /*
     * LUẬT (người dùng chốt từng vế):
     *   · hoá thành dã trư: ATK/ HP/ DEF tăng MẠNH nhưng tốc đánh GIẢM MẠNH;
     *   · đánh thường biến thành TIẾNG GẦM diện rộng gây CHOÁNG — ⚠️ KHÔNG choáng boss;
     *   · mỗi 2s NUỐT 1 con đang bị nhắm — ⚠️ KHÔNG nuốt boss;
     *   · duy trì 15 giây.
     */
    desc: 'Hoá dã trư khổng lồ 15s: ATK/ máu/ giáp tăng mạnh nhưng tốc đánh giảm mạnh. Đánh thường thành tiếng gầm diện rộng gây choáng (không choáng boss); mỗi 2s nuốt 1 quái đang bị nhắm (không nuốt boss).',
    duration: 15,
    cooldown: ULT_COOLDOWN,
    color: 0xb45309,
  },

  // ------------------------------------------------------------------ Gái ngành
  agility: {
    id: 'ultClone',
    name: 'Phân Thân',
    desc: 'Tạo 3 bản sao giống hệt rải khắp bản đồ trong 20s, tốc đánh tăng mạnh. Bản sao tự động dùng skill — bạn chỉ điều khiển con gốc.',
    duration: 20,
    cooldown: ULT_COOLDOWN,
    color: 0x38bdf8,
  },

  // ------------------------------------------------------------------ Dân chợ búa
  street: {
    id: 'ultFirebomb',
    name: 'Ném Bom Xăng',
    desc: 'Chạy lung tung ném bom xăng liên tục trong 20s: mỗi vùng ném 1 quả rồi chuyển sang vùng khác, ưu tiên vùng nhiều quái nhất. Bom thiêu đốt và giảm giáp.',
    duration: 20,
    cooldown: ULT_COOLDOWN,
    color: 0xf97316,
  },

  // ------------------------------------------------------------------ Bắn thuê
  army: {
    id: 'ultAirstrike',
    name: 'Gọi Máy Bay',
    desc: 'Gọi máy bay bay qua bay lại thả bom xuống đúng nơi nó bay qua, trong 20s.',
    duration: 20,
    cooldown: ULT_COOLDOWN,
    color: 0x6366f1,
  },

  // ------------------------------------------------------------------ Đẻ mướn
  mother: {
    id: 'ultSubjugate',
    name: 'Thu Phục',
    /*
     * `duration: 0` = KHÔNG đếm ngược (xem chú thích `UltimateDef.duration`).
     *
     * LUẬT (người dùng chốt rất kỹ):
     *   · biến 20% số quái ĐANG CÓ TRÊN MÀN thành quân mình — chọn RANDOM, KHÔNG ưu tiên con gần;
     *   · ⚠️ TRỪ BOSS (không thu phục được boss);
     *   · KHÔNG đếm lùi: chỉ kết thúc khi con bị thu phục chết; hết hiệu lực/ hết màn thì chúng
     *     TRỞ LẠI làm quái;
     *   · ⚠️ số quân thu phục KHÔNG tính vào trần 15 con đẻ.
     */
    desc: 'Thu phục 20% số quái đang có trên màn thành quân mình (chọn ngẫu nhiên, trừ boss). Không đếm ngược — chỉ kết thúc khi con bị thu phục chết; khi hết hiệu lực hoặc hết màn thì chúng trở lại làm quái. Quân thu phục không tính vào trần 15 con đẻ.',
    duration: 0,
    cooldown: ULT_COOLDOWN,
    color: 0xdb2777,
  },

  /*
   * ==============================================================================================
   * 2 CLASS KHUNG PLACEHOLDER (Thợ mộc · Thợ lặn) — ultimate TẠM.
   *
   * `ClassDef` của 2 class này hiện vẫn còn 7 chiêu placeholder (xem khối TODO(design) ở
   * `data/classes.ts`); người dùng chốt *"design, skill, mọi thứ mình sẽ làm sau"*.
   *
   * ⇒ Ultimate ở đây là TẠM, chỉ để `Record<ClassId, …>` đủ khoá và game không vỡ. Bản sao TINH
   *   THẦN của ultimate đã có sẵn (thợ mộc → Dã Trư của tank; thợ lặn → Phân Thân của sát thủ),
   *   KHÔNG nhằm mục đích cân bằng. Sẽ thiết kế lại cùng lúc với 7 chiêu của 2 class.
   * ==============================================================================================
   */

  // TODO(design): ultimate TẠM — xem khối chú thích ngay trên.
  carpenter: {
    id: 'ultWoodTitan',
    name: 'Tượng Gỗ Khổng Lồ',
    desc: 'Hoá thành tượng gỗ khổng lồ 15s: giáp và máu tăng mạnh nhưng tốc đánh giảm mạnh. Đánh thường thành tiếng nện diện rộng gây choáng (không choáng boss); mỗi 2s nuốt 1 quái đang bị nhắm (không nuốt boss).',
    duration: 15,
    cooldown: ULT_COOLDOWN,
    color: 0x92400e,
  },

  // TODO(design): ultimate TẠM — xem khối chú thích phía trên.
  diver: {
    id: 'ultDiveClones',
    name: 'Bóng Nước Phân Thân',
    desc: 'Tạo 3 bản sao bằng nước rải khắp bản đồ trong 20s, tốc đánh tăng mạnh. Bản sao tự động dùng skill — bạn chỉ điều khiển con gốc.',
    duration: 20,
    cooldown: ULT_COOLDOWN,
    color: 0x0891b2,
  },
};

/** Ultimate của một class — tra nhanh cho HUD (không bao giờ undefined vì `Record` đủ khoá). */
export function ultimateOf(classId: ClassId): UltimateDef {
  return ULTIMATES[classId];
}
